import React, { useRef, forwardRef, useMemo, useContext, useEffect} from "react"

import { TextureLoader } from "three"
import {  useLoader, useThree, useFrame } from "react-three-fiber"
import { useSpring, a } from "@react-spring/three"

import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader'

import * as THREE from 'three'
import { CustomShader } from '../../resources/shaders/CustomShader'
import { AppContext } from '../../context'
import Element from "./element"
import { useTextureLoader } from "./useTextureLoader"

import noiseImg from '../../assets/noise.png';

const ImagePlane = forwardRef(({ color = "white", shift = 1, opacity = 1, args, map, alphaMap, additiveBlend,...props }, ref) => {
    const material = useRef()
    return (
        <a.mesh ref={ref} {...props} >
            <planeBufferGeometry attach="geometry" args={args} />
            <a.meshBasicMaterial ref={material} attach="material" color={color} map={map} alphaMap={alphaMap} transparent={true} opacity={opacity} blending={additiveBlend ? THREE.AdditiveBlending : THREE.NormalBlending}/>
        </a.mesh>
    )
})

const useNoiseTexture = () => {
    const context = useContext(AppContext);
    return useMemo(() => {
        if (!context.textures.noiseTexture) {
            const loader = new THREE.TextureLoader()
            context.textures.noiseTexture = loader.load(noiseImg);
     //       console.log ("[Load noise texture]", context.textures.noiseTexture)
        }
   //     console.log ("[Use noise texture]", context.textures.noiseTexture)
        return context.textures.noiseTexture;
    },[])
}

const ImageWebgl = forwardRef(({ image, keyart, zoomFactor, intensity, active, hovered = 0, opacity = 1, shift = 1, color = "white", additiveBlend, args, map, elements = [], ...props }, ref) => {
    const { progress } = useSpring({ progress: active ? 1 : 0 })
    const glitch = props.glitch || (hovered && !active) ? 1 : 0;
    const { gl, invalidate, } = useThree()
    const context = useContext(AppContext);
    const material = useRef();
    const texturesRef = useRef([]);

    useEffect(() => {
 //       console.log('mount ImageWebgl name', props.name, ref.current, props);
        return () => {
 //           console.log('unmout ImageWebgl name', props.name, ref.current, props);
            if (texturesRef.current) {
                texturesRef.current.forEach(texture => {
          //          console.log ("[useTextureLoader] dispose", texture);
                    texture.dispose();                    
                })
                texturesRef.current = null;
            }
            if (material.current) {
                material.current.dispose();
                material.current = null;
            }
        }
    }, []);

    useFrame(({ clock }, delta) => {
        const { amount, angle } = context.offset;

        material.current.uniforms.amount.value = THREE.Math.clamp(amount, 0, 1);
        material.current.uniforms.angle.value = angle;
        material.current.uniforms.time.value = clock.getElapsedTime();

        if (glitch) {
            const factor = 0.5;
            material.current.uniforms.amount.value = (Math.random() / 120) * factor
            material.current.uniforms.angle.value = THREE.Math.randFloat(-Math.PI, Math.PI) * factor
            material.current.uniforms.distortion_x.value = THREE.Math.randFloat(0.01, 0.02) * factor
            material.current.uniforms.distortion_y.value = THREE.Math.randFloat(0.01, 0.02) * factor
            material.current.uniforms.seed_x.value = THREE.Math.randFloat(0.0, 0.01) * factor
            material.current.uniforms.seed_y.value = THREE.Math.randFloat(0.0, 0.01) * factor
        }
        //console.log(material.current.uniforms.shift.value);
    })

    //console.log (image, opacity);
    const disp = useNoiseTexture();
    
    const margs = useMemo(() => {
        const { effectMask, image} = keyart || {};
        const loader = new THREE.TextureLoader()
        //const disp = loader.load(noiseImg, invalidate)
        const mapMask = effectMask ? loader.load(effectMask, invalidate) : null;
        const mapFull = image ? loader.load(image, invalidate) : null;
        if (mapFull) {
            mapFull.anisotropy = gl.capabilities.getMaxAnisotropy();
        }  
        texturesRef.current = [mapMask,mapFull].filter(item => item);      
        const [sizeX, sizeY] = [64, 64];
        const factor = 1;
        return {
            uniforms: {
                effectFactor: { type: 'f', value: intensity },
                dispFactor: { type: 'f', value: 0 },
                texture1: { type: 't', value: map },
                texture2: { type: 't', value: mapFull },
                disp: { type: 't', value: disp },
                mask: { type: 't', value: mapMask },
                scale: { type: 'f', value: 0 },
                opacity: { type: 'f', value: 1 },
                time: { value: 0 },
                resolution: { value: new THREE.Vector2(sizeX, sizeY) },
                glitch: { type: 'f', value: 0 },
                amount: { type: 'f', value: (Math.random() / 120) * factor },
                angle: { type: 'f', value: THREE.Math.randFloat(-Math.PI, Math.PI) * factor },
                distortion_x: { type: 'f', value: THREE.Math.randFloat(0.01, 0.02) * factor },
                distortion_y: { type: 'f', value: THREE.Math.randFloat(0.01, 0.02) * factor },
                seed_x: { type: 'f', value: THREE.Math.randFloat(0.0, 0.01) * factor },
                seed_y: { type: 'f', value: THREE.Math.randFloat(0.0, 0.01) * factor },
                color: { value: new THREE.Color("white") },
            },
            vertexShader: CustomShader.vertexShader,
            fragmentShader: CustomShader.fragmentShader,
        }
    }, [keyart, intensity, gl.capabilities, invalidate, map])
   
    //console.log (elements);

    const elementImages = useTextureLoader(elements.map(({ image }) => image));
    
    return (
        <a.group {...props} >
            <a.mesh ref={ref}  userData={{zoomFactor, ...keyart}}>
                <a.planeBufferGeometry attach="geometry" args={args} />
                <a.shaderMaterial ref={material} attach="material" args={[margs]} uniforms-color-value={color} uniforms-dispFactor-value={progress} transparent uniforms-opacity-value={opacity} uniforms-glitch-value={glitch} blending={additiveBlend ? THREE.AdditiveBlending : THREE.NormalBlending}
                />
            </a.mesh>
            {active && elements.map(({ratio, size, ...props }, index) => {
                const {ratio: kr = 1, size: ks = 1} = keyart||{}
                return <Element sub={true} key={index} map={elementImages[index]} {...props} ratio={ratio/kr} size={size/ks} />
            }
            )}
        </a.group>
    )
})

const Shape = ({ color, shape, fillOpacity=0, offset=[0,0,0], scale=[1,1,1], ...props}) => {
    return (
        <a.mesh position={offset} scale={scale} {...props}>
            <a.meshBasicMaterial attach="material" color={color} opacity={fillOpacity} depthWrite={false} transparent />
            <shapeBufferGeometry attach="geometry" args={[shape]} />
        </a.mesh>
    )
}

const Svg = forwardRef(({ url, position, groupScale, flipX, flipY, ...props }, ref) => {
    const { paths } = useLoader(SVGLoader, url)
    const shapes = useMemo(
        () => paths.flatMap((p, i) => p.toShapes(true).map(shape => ({ shape, color: p.color, fillOpacity: p.userData.style.fillOpacity }))),
        [paths]
    )
    // console.log(shapes);
    return (
        <group position={[0,0,0]} scale={[flipX?-1:1,flipY?-1:1,1]} ref={ref}>
            <group position={position} scale={groupScale} >
                {shapes.map(p=> (
                    <Shape key={p.shape.uuid} {...p} {...props}/>
                ))}
            </group>
        </group>
    )
})

const Plane = forwardRef(({...props }, ref) => {
    return props.interactivable || props.glitch  ? (
        <ImageWebgl ref={ref} {...props} />
    ) : (
        <ImagePlane ref={ref} {...props} />
    );
})
export { Plane, ImageWebgl, ImagePlane, Svg}
